import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { aquaApi } from '../api/aquaApi'
import { handleAxiosCodeError, handleAxiosError } from '../helpers/handleAxiosError'
import { AxiosError } from 'axios'

const getData = async <T>(url: string) => {
  const { data } = await aquaApi.get<T>(url)
  return data
}

interface Args<T> {
  url: string
  enabled: boolean
  cleanCache?: boolean
  onSuccess?: (data: T) => void
}

export const useGet = <T>({ url, enabled, cleanCache = true, onSuccess }: Args<T>) => {
  const queryClient = useQueryClient()
  const { data, isLoading, error, refetch, isSuccess } = useQuery({
    queryKey: [url],
    queryFn: () => getData<T>(url),
    enabled,
  })

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      if (onSuccess) onSuccess(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data])

  const getInitialData = async () => {
    const result = await refetch()
    if (result.isSuccess && result.data !== undefined) {
      if (onSuccess) onSuccess(result.data)
    }
    if (cleanCache) queryClient.clear()
  }

  return {
    getInitialData,
    msgError: handleAxiosError(error as AxiosError),
    codeError: handleAxiosCodeError(error as AxiosError),
    isLoading,
    initialData: data,
    isSuccess,
  }
}
